'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProductOption, ProductVariant, ShopifyImage as ShopifyImageType } from '@repo/shopify';
import { Skeleton } from '@repo/ui/components/skeleton';
import { ShieldCheck, Truck } from 'lucide-react';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import { Fragment, type ReactNode, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useCart, useCartActions, useCartSummary } from '@/components/cart/cart-provider';
import { salePercentOff } from '@/components/product/product-badges';
import { ShopifyImage } from '@/components/ui/shopify-image';
import { StarIcon } from '@/components/ui/star-icon';
import type { ApparelBundleOffer } from '@/lib/apparel-bundle-config';
import type { HydrateBundleOffer } from '@/lib/insider-bundle-config';
import { formatMoney, formatPerUnitPrice } from '@/lib/money';
import type { ProductTypeDetail, TrustBadge } from '@/lib/product-page-settings';
import type { ProductPageClientProduct } from '@/lib/product-page-types';
import { getDefaultSellingPlan, getSellingPlanLabel, getSellingPlanPercentOff, getSellingPlanUnitPrice, getSubscriptionPlans } from '@/lib/selling-plans';
import { DEFAULT_SUBSCRIBE_SAVE_SETTINGS, type SubscribeSaveConfig } from '@/lib/subscribe-save-config';
import { formatMembershipCadence, pickToolboxSize, TOOLBOX_MEMBERSHIP_DETAILS, type ToolboxMembershipOffer } from '@/lib/toolbox-membership';
import type { ApparelBundlePick } from './apparel-bundle/apparel-bundle-math';
import { ApparelBundleStrip } from './apparel-bundle/apparel-bundle-strip';
import { useApparelBundleState } from './apparel-bundle/use-apparel-bundle-state';
import { BackInStockNotify } from './back-in-stock-notify';
import type { GiftSelection } from './gift-variant-modal';
import { type BuyBoxMode, BuyBoxModeTabs } from './hydrate-bundle/buy-box-mode-tabs';
import { getTotalSticks } from './hydrate-bundle/hydrate-bundle-math';
import { useHydrateBundleState } from './hydrate-bundle/use-hydrate-bundle-state';
import { LinkedProductColorSelector } from './linked-product-color-selector';
import { buildVariantSearchParams, getColorOption, getDefaultSelectedOptions, getSelectedOptionsFromSearchParams, type ProductVariantSelectionLike, resolveSelectedOptions } from './product-option-utils';
import { ProductStyleSelector } from './product-style-selector';
import { ProductSwatchGroup, type SwatchGroupItem } from './product-swatch-group';
import { PurchaseTrustList } from './purchase-trust-list';
import { buildGiftExtraLines, buildGiftSelections } from './subscribe-save-gift-lines';
import { ToolboxMembershipRow } from './toolbox-membership-row';
import { VariantSelector } from './variant-selector';

// Load all subscription controls together for eligible products, including
// controls behind the Crew Pack tab. Switching modes then needs no new chunk.
// SSR stays enabled so those products keep their complete initial buy box.
const SubscriptionProductInfo = dynamic(() => import('./subscription-product-info').then(module => module.SubscriptionProductInfo));

/**
 * The gift size sheet, loaded on demand for one-time products that offer the
 * Toolbox membership — subscription products get it via SubscriptionProductInfo.
 */
const LazyGiftVariantModal = dynamic(() => import('./gift-variant-modal').then(module => module.GiftVariantModal));
interface LinkedColorProduct {
  handle: string;
  title: string;
  options: ProductOption[];
  variants: ProductVariantSelectionLike[];
  colorValues: string[];
  colorOptionName: string | null;
}

/** The slice of ProductTypeDetail the buy box uses (tagline + fit indicator). */
export type ProductInfoTypeDetails = Pick<ProductTypeDetail, 'tagline' | 'fitScale'>;
export interface ProductInfoProps {
  product: ProductPageClientProduct;
  variants: ProductVariant[];
  /**
   * True while `variants` availability is cached-only (the Suspense fallback of
   * the live-availability pass). A cached sold-out claim renders as a disabled
   * ADD TO CART instead of a definitive SOLD OUT, so stale cache never flashes
   * the wrong state before live inventory streams in.
   */
  availabilityPending?: boolean;
  initialSelectedOptions: Record<string, string>;
  siblings?: {
    handle: string;
    title: string;
    productType: string;
    featuredImage: ShopifyImageType | null;
    colorValues: string[];
    images: ShopifyImageType[];
    variants: ProductVariantSelectionLike[];
    discountPercent: number | null;
    keepAcrossColors?: boolean;
  }[];
  linkedColorProducts?: LinkedColorProduct[];
  /**
   * Print/pattern or flavor swatches sourced from a `swatch-group:*` /
   * `flavor-group:*` tag (separate products).
   */
  swatchGroup?: SwatchGroupItem[];
  /** Selector heading — "Design" for apparel prints, "Flavor" for supplements. */
  swatchGroupLabel?: string;
  /**
   * H1 / sticky-ATC title. Flavor groups use the base product name
   * (e.g. "Workman Hydrate") while Shopify `product.title` keeps the full
   * name for cart line items and analytics.
   */
  displayTitle?: string;
  onSelectedColorChange?: (color: string | null) => void;
  enableReviews?: boolean;
  moneyLocale?: string;
  /** Star rating from Klaviyo Reviews API */
  rating?: number | null;
  /** Total review count from Klaviyo Reviews API */
  reviewCount?: number | null;
  /** Optional server-streamed review summary for first paint */
  reviewSummarySlot?: ReactNode;
  /** Server-rendered CMS sizing chart (SizingChartContent); null hides the link. */
  sizingChartSlot?: ReactNode;
  /** CMS product type details — only the fields the buy box renders. */
  typeDetails?: ProductInfoTypeDetails | null;
  /** CMS-driven Subscribe & Save labels/benefits, resolved for this product */
  subscribeSave?: SubscribeSaveConfig | null;
  /** Crew Pack offer (config + flavors + kit product); null hides the bundle tab. */
  bundle?: HydrateBundleOffer | null;
  /** Apparel Bundle & Save ladder (cart-driven); null hides the strip. */
  apparelBundle?: ApparelBundleOffer | null;
  /** Toolbox tee-of-the-month membership offered under the CTA; null hides the row. */
  toolbox?: ToolboxMembershipOffer | null;
  /** CMS-driven trust badges for this product type; null falls back to the built-in pair */
  trustBadges?: TrustBadge[] | null;
  /** Featured review to display as social proof */
  featuredReview?: {
    author: string;
    content: string;
    rating: number;
    verified: boolean;
  } | null;
  /** Optional server-streamed featured review for first paint */
  featuredReviewSlot?: ReactNode;
}
interface PendingRequestedSelection {
  searchParamsString: string;
  selectedOptions: Record<string, string>;
}
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Shown under the CTA when the CMS has no trust badges configured. */
const DEFAULT_TRUST_BADGES: TrustBadge[] = [{
  label: 'Printed in America',
  icon: 'flag'
}, {
  label: '30-Day Returns',
  icon: 'clock'
}];
function TrustBadgeIcon(t0) {
  const $ = _c(5);
  const {
    icon
  } = t0;
  switch (icon) {
    case "clock":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case "shield":
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <ShieldCheck className="h-4 w-4" aria-hidden="true" />;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "truck":
      {
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Truck className="h-4 w-4" aria-hidden="true" />;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    case "star":
      {
        let t1;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <StarIcon className="h-4 w-4" aria-hidden="true" />;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    default:
      {
        let t1;
        if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2" y="4" width="20" height="16" stroke="currentColor" strokeWidth="2" /><path d="M2 8h20 M2 12h20 M2 16h20" stroke="currentColor" strokeWidth="2" /><rect x="2" y="4" width="10" height="8" fill="currentColor" /></svg>;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
  }
}
function StarRating(t0) {
  const $ = _c(9);
  const {
    rating,
    count
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [1, 2, 3, 4, 5];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== rating) {
    t2 = <div className="flex gap-0.5">{t1.map(n => <StarIcon key={`rating-star-${n}`} className={`h-4 w-4 ${n <= Math.round(rating) ? "fill-neutral-900 text-neutral-900" : "fill-neutral-200 text-neutral-200"}`} />)}</div>;
    $[1] = rating;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== count || $[4] !== rating) {
    t3 = <span className="text-sm text-neutral-600">{rating} ({count} Reviews)</span>;
    $[3] = count;
    $[4] = rating;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <button type="button" className="flex items-center gap-2 transition-opacity hover:opacity-70" onClick={_temp}>{t2}{t3}</button>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp() {
  document.getElementById("reviews")?.scrollIntoView({
    behavior: "smooth"
  });
  window.history.replaceState(window.history.state, "", "#reviews");
}
export function ProductInfoSkeleton() {
  const $ = _c(19);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center justify-between"><div className="flex items-center gap-2"><div className="flex gap-0.5">{[1, 2, 3, 4, 5].map(_temp2)}</div><Skeleton className="h-4 w-24" /></div><Skeleton className="h-6 w-12" /></div>;
    t1 = <Skeleton className="mt-2 h-8 w-3/4 lg:h-9" />;
    t2 = <Skeleton className="mt-2 h-7 w-24" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Skeleton className="h-4 w-12" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div>{t3}<div className="mt-3 flex flex-wrap gap-3">{[1, 2, 3].map(_temp3)}</div></div>;
    t5 = <Skeleton className="h-4 w-28" />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div>{t5}<div className="mt-2 flex gap-2">{[1, 2, 3].map(_temp4)}</div></div>;
    t7 = <Skeleton className="h-4 w-24" />;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div>{t7}<div className="mt-2 grid grid-cols-4 gap-1.5">{[1, 2, 3, 4].map(_temp5)}</div></div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex items-center justify-between"><Skeleton className="h-3 w-8" /><Skeleton className="h-3 w-24" /></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="rounded-md border border-neutral-200 px-2.5 py-2">{t9}<div className="mt-2.5 flex items-center justify-between"><Skeleton className="h-2.5 w-16" /><Skeleton className="h-2.5 w-16" /></div><Skeleton className="mt-2 h-px w-full rounded-full" /></div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Skeleton className="h-12 w-full" />;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex items-center gap-1.5"><Skeleton className="h-5 w-5" /><Skeleton className="h-3 w-24" /></div>;
    t13 = <Skeleton className="h-3 w-2" />;
    $[12] = t12;
    $[13] = t13;
  } else {
    t12 = $[12];
    t13 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="space-y-3">{t11}<div className="flex items-center justify-center gap-3">{t12}{t13}<div className="flex items-center gap-1.5"><Skeleton className="h-4 w-4" /><Skeleton className="h-3 w-20" /></div></div></div>;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  let t16;
  let t17;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="flex justify-center gap-0.5">{[1, 2, 3, 4, 5].map(_temp6)}</div>;
    t16 = <Skeleton className="mx-auto mt-1.5 h-4 w-full max-w-[20rem]" />;
    t17 = <Skeleton className="mx-auto mt-1 h-4 w-4/5" />;
    $[15] = t15;
    $[16] = t16;
    $[17] = t17;
  } else {
    t15 = $[15];
    t16 = $[16];
    t17 = $[17];
  }
  let t18;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="flex flex-col">{t0}{t1}{t2}<div className="mt-5 space-y-4">{t4}{t6}{t8}{t10}{t14}<div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2.5">{t15}{t16}{t17}<div className="mt-1.5 flex items-center justify-center gap-2"><Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-24" /></div></div></div></div>;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  return t18;
}
function _temp6(n_3) {
  return <Skeleton key={`featured-star-${n_3}`} className="h-3.5 w-3.5 rounded-sm" />;
}
function _temp5(n_2) {
  return <Skeleton key={`size-${n_2}`} className="h-11 w-full rounded" />;
}
function _temp4(n_1) {
  return <Skeleton key={`color-${n_1}`} className="h-8 w-8 rounded-full" />;
}
function _temp3(n_0) {
  return <div key={`style-${n_0}`} className="w-16 md:w-[4.5rem]"><Skeleton className="h-14 w-full rounded-sm md:h-16" /><Skeleton className="mt-px h-5 w-full rounded-none" /></div>;
}
function _temp2(n) {
  return <Skeleton key={`rating-star-${n}`} className="h-4 w-4 rounded-sm" />;
}
function PriceWithCents(t0) {
  const $ = _c(9);
  const {
    amount,
    currencyCode,
    locale: t1,
    className
  } = t0;
  const locale = t1 === undefined ? "en-US" : t1;
  let t2;
  let t3;
  if ($[0] !== amount || $[1] !== className || $[2] !== currencyCode || $[3] !== locale) {
    const num = Number.parseFloat(amount);
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode
    }).formatToParts(num);
    t2 = className;
    t3 = parts.map(_temp7);
    $[0] = amount;
    $[1] = className;
    $[2] = currencyCode;
    $[3] = locale;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

/** Optimistic line info + add options for the buy-box add-to-cart call. */
function _temp7(part) {
  return part.type === "decimal" ? null : part.type === "fraction" ? <sup key={`${part.type}-${part.value}`} className="text-[0.6em] align-super ml-0.5">{part.value}</sup> : <span key={`${part.type}-${part.value}`}>{part.value}</span>;
}
function buildAddToCartPayload({
  product,
  selectedVariant,
  selectedSellingPlan,
  giftLines
}: {
  product: ProductPageClientProduct;
  selectedVariant: ProductVariant;
  selectedSellingPlan: ReturnType<typeof getSubscriptionPlans>[number] | null;
  giftLines: ReturnType<typeof buildGiftExtraLines>;
}) {
  return {
    productInfo: {
      productId: product.id,
      title: product.title,
      handle: product.handle,
      vendor: product.vendor,
      productType: product.productType,
      image: selectedVariant.image || product.featuredImage,
      variantTitle: selectedVariant.title === 'Default Title' ? undefined : selectedVariant.title,
      sku: selectedVariant.sku,
      price: selectedSellingPlan ? getSellingPlanUnitPrice(selectedVariant.price, selectedSellingPlan) : selectedVariant.price
    },
    options: {
      analyticsSource: 'product_page',
      // name = derived label so the optimistic drawer line shows the real
      // frequency (Recharge plan names can be wrong, see getSellingPlanLabel).
      sellingPlan: selectedSellingPlan ? {
        id: selectedSellingPlan.id,
        name: getSellingPlanLabel(selectedSellingPlan)
      } : undefined,
      extraLines: giftLines.length > 0 ? giftLines : undefined
    }
  };
}
function variantMatchesSelectedOptions(variant: Pick<ProductVariant, 'selectedOptions'>, selectedOptions: Record<string, string>) {
  return variant.selectedOptions.every(option => selectedOptions[option.name] === option.value);
}
function isProductPathForHandle(pathname: string, handle: string) {
  return pathname.replace(/\/$/, '').endsWith(`/products/${handle}`);
}
function deleteOptionSearchParams(params: URLSearchParams) {
  for (const key of Array.from(params.keys()).filter(key => key.startsWith('option.'))) {
    params.delete(key);
  }
}
function hasSelectionSearchParams(params: URLSearchParams) {
  if (params.has('variant')) return true;
  for (const key of params.keys()) {
    if (key.startsWith('option.')) return true;
  }
  return false;
}
function shouldWaitForRequestedSelection(variant: Pick<ProductVariant, 'selectedOptions'>, pendingSelection: PendingRequestedSelection | null, searchParamsString: string) {
  return pendingSelection?.searchParamsString === searchParamsString && !variantMatchesSelectedOptions(variant, pendingSelection.selectedOptions);
}
function getCurrentBrowserSearchParams(fallbackSearchParamsString: string) {
  if (typeof window === 'undefined') {
    return new URLSearchParams(fallbackSearchParamsString);
  }
  return new URLSearchParams(window.location.search);
}
function replaceBrowserUrlWithoutNavigation(nextUrl: string) {
  // Use the native prototype method so selection-only URL sync does not notify
  // patched history listeners that treat `?variant=` as a page navigation.
  const replaceState = typeof History !== 'undefined' ? History.prototype.replaceState : window.history.replaceState;
  replaceState.call(window.history, window.history.state, '', nextUrl);
}
function getVariantUrlSyncTarget({
  pathname,
  searchParamsString,
  selectedVariant,
  syncVariantUrl
}: {
  pathname: string;
  searchParamsString: string;
  selectedVariant: ProductVariant;
  syncVariantUrl: boolean;
}) {
  const browserParams = getCurrentBrowserSearchParams(searchParamsString);
  if (!syncVariantUrl && !hasSelectionSearchParams(browserParams)) return null;
  const variantId = buildVariantSearchParams(selectedVariant).get('variant');
  if (!variantId) return null;
  const currentParams = new URLSearchParams(browserParams);
  deleteOptionSearchParams(currentParams);
  const nextParams = new URLSearchParams(browserParams);
  deleteOptionSearchParams(nextParams);
  nextParams.set('variant', variantId);
  const query = nextParams.toString();
  if (currentParams.toString() === query) return null;
  return {
    query,
    url: query ? `${pathname}?${query}` : pathname
  };
}
function resolveOptionsAfterOptionChange(variants: ProductVariant[], currentOptions: Record<string, string>, name: string, value: string) {
  const nextRequestedOptions = {
    ...currentOptions,
    [name]: value
  };
  const resolvedOptions = resolveSelectedOptions(variants, nextRequestedOptions);
  if (name.toLowerCase() !== 'color' || resolvedOptions[name] === value) {
    return resolvedOptions;
  }
  return resolveSelectedOptions(variants, {
    [name]: value
  });
}
function FitIndicator(t0) {
  const $ = _c(14);
  const {
    typeDetails,
    sizingChartSlot
  } = t0;
  if (!typeDetails?.fitScale) {
    return null;
  }
  const position = Math.min(5, Math.max(1, typeDetails.fitScale));
  const hasSizingChart = !!sizingChartSlot;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-[11px] font-semibold text-neutral-900">Size</p>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== hasSizingChart || $[2] !== sizingChartSlot) {
    t2 = hasSizingChart && <SizingChartDialog>{sizingChartSlot}</SizingChartDialog>;
    $[1] = hasSizingChart;
    $[2] = sizingChartSlot;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="flex items-center justify-between">{t1}{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mt-2.5 flex items-center justify-between text-[10px] leading-none text-neutral-500"><span>Fits too small</span><span>Fits too large</span></div>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="absolute left-0 right-0 top-1/2 z-0 h-px -translate-y-1/2 bg-neutral-300" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [1, 2, 3, 4, 5];
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== position) {
    t7 = <div className="relative mt-1">{t5}<div className="relative z-10 flex items-center justify-between">{t6.map(n => <div key={`fit-${n}`} className={`h-2 w-2 rounded-sm ${n === position ? "bg-neutral-900" : "border border-neutral-300 bg-white"}`} />)}</div></div>;
    $[9] = position;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t3 || $[12] !== t7) {
    t8 = <div className="rounded-md border border-neutral-200 px-2.5 py-2">{t3}{t4}{t7}</div>;
    $[11] = t3;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
function FeaturedReview(t0) {
  const $ = _c(16);
  const {
    review
  } = t0;
  if (!review) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [1, 2, 3, 4, 5];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== review.rating) {
    t2 = <div className="flex justify-center gap-0.5">{t1.map(n => <StarIcon key={`review-star-${n}`} className={`h-3.5 w-3.5 ${n <= Math.round(review.rating) ? "fill-brand-accent text-brand-accent" : "fill-neutral-200 text-neutral-200"}`} />)}</div>;
    $[1] = review.rating;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== review.content) {
    t3 = <p className="mt-1.5 text-center text-[13px] leading-snug text-neutral-700">“{review.content}”</p>;
    $[3] = review.content;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== review.author) {
    t4 = <span className="font-semibold text-neutral-900">{review.author}</span>;
    $[5] = review.author;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== review.verified) {
    t5 = review.verified && <span className="flex items-center gap-1 text-neutral-600"><svg className="h-3.5 w-3.5 flex-shrink-0 text-neutral-900" viewBox="0 0 22 22" aria-hidden="true"><path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.855-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.691-.13.635-.08 1.293.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.604-.274 1.262-.144 1.897.13.636.433 1.221.878 1.69.47.446 1.055.749 1.69.879.635.13 1.294.084 1.902-.139.272.587.702 1.087 1.24 1.443s1.167.554 1.813.571c.646-.017 1.272-.215 1.812-.571s.97-.856 1.245-1.443c.604.223 1.262.27 1.897.139.634-.13 1.218-.433 1.687-.879.445-.469.75-1.054.882-1.69.132-.635.083-1.293-.14-1.897.586-.273 1.084-.705 1.439-1.246.355-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z" fill="currentColor" /></svg>Verified Buyer</span>;
    $[7] = review.verified;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-[13px]">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t6) {
    t7 = <div className="rounded border border-neutral-200 bg-neutral-50 p-4">{t2}{t3}{t6}</div>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function SizingChartDialog(t0) {
  const $ = _c(8);
  const {
    children
  } = t0;
  const dialogRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      dialogRef.current?.showModal();
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleOpen = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      dialogRef.current?.close();
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClose = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <button type="button" className="text-[11px] font-medium text-neutral-500 underline underline-offset-2 transition-colors hover:text-neutral-900" onClick={handleOpen}>View Sizing Chart</button>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => {
      if (e.target === dialogRef.current) {
        handleClose();
      }
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900">Sizing Chart</h2>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-2">{t5}<button type="button" className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-900" onClick={handleClose} aria-label="Close sizing chart"><svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" /></svg></button></div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== children) {
    t7 = <>{t3}<dialog ref={dialogRef} className="m-auto w-[calc(100%-1rem)] max-w-2xl rounded border-0 p-0 shadow-2xl backdrop:bg-black/60 sm:w-full" onClick={t4}><div className="relative">{t6}{children}</div></dialog></>;
    $[6] = children;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
}
export function ProductInfo(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = props.bundle || getSubscriptionPlans(props.product).length > 0 ? <SubscriptionProductInfo {...props} /> : <ProductInfoBase {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
interface SubscriptionComponents {
  HydrateBundlePanel: typeof import('./hydrate-bundle/hydrate-bundle-panel').HydrateBundlePanel;
  PurchaseOptionSelector: typeof import('./purchase-option-selector').PurchaseOptionSelector;
  GiftVariantModal: typeof import('./gift-variant-modal').GiftVariantModal;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: product detail state combines variant selection, pricing, and add-to-cart flows
export function ProductInfoBase({
  product,
  variants,
  availabilityPending = false,
  initialSelectedOptions,
  siblings = [],
  linkedColorProducts = [],
  swatchGroup = [],
  swatchGroupLabel = 'Design',
  displayTitle,
  onSelectedColorChange,
  enableReviews = false,
  moneyLocale,
  rating: ratingProp,
  reviewCount: reviewCountProp,
  reviewSummarySlot,
  sizingChartSlot,
  typeDetails,
  subscribeSave,
  bundle,
  apparelBundle,
  toolbox,
  trustBadges,
  featuredReview,
  featuredReviewSlot,
  subscriptionComponents
}: ProductInfoProps & {
  subscriptionComponents?: SubscriptionComponents;
}) {
  const HydrateBundlePanel = subscriptionComponents?.HydrateBundlePanel;
  const PurchaseOptionSelector = subscriptionComponents?.PurchaseOptionSelector;
  const GiftVariantModal = subscriptionComponents?.GiftVariantModal ?? (toolbox ? LazyGiftVariantModal : undefined);
  const {
    addItem,
    getCart,
    removeItem
  } = useCartActions();
  const {
    cart: liveCart
  } = useCart();
  const {
    isOpen: isCartOpen
  } = useCartSummary();
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(initialSelectedOptions);
  const [_addedToCart, setAddedToCart] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showStickyATC, setShowStickyATC] = useState(false);
  const [shouldSyncVariantUrl, setShouldSyncVariantUrl] = useState(false);
  // Sold-out option (e.g. a sold-out size) the shopper tapped to open the Back in
  // Stock prompt. Kept separate from `selectedOptions` so it never reaches the
  // purchase path or URL sync (both of which deliberately drop sold-out variants).
  const [notifySelection, setNotifySelection] = useState<{
    name: string;
    value: string;
  } | null>(null);
  // Multi-variant gifts awaiting option selection after a subscription add.
  const [giftSelections, setGiftSelections] = useState<GiftSelection[]>([]);
  // Insider Bundle vs single box. One box is the default everywhere — the
  // shopper opts into the bundle tab — so reloads and flavor-swatch
  // navigations never flash or reset into bundle mode. Bundle purchase state
  // is lifted here so the headline price row mirrors it while the tab is
  // active.
  const [buyBoxMode, setBuyBoxMode] = useState<BuyBoxMode>('single');
  const isBundleMode = !!bundle && buyBoxMode === 'bundle';
  const bundleState = useHydrateBundleState(bundle, subscribeSave ?? DEFAULT_SUBSCRIBE_SAVE_SETTINGS);
  // Subscribe & Save — recurring plans synced from the subscription app. When
  // non-null, add-to-cart attaches the line to this selling plan.
  const subscriptionPlans = useMemo(() => getSubscriptionPlans(product), [product]);
  const subscribeSaveConfig = subscribeSave ?? DEFAULT_SUBSCRIBE_SAVE_SETTINGS;
  // Subscription-only products keep the selector even when the CMS disables it —
  // Shopify rejects a one-time add for them.
  const showSubscribeSave = subscriptionPlans.length > 0 && (subscribeSaveConfig.enabled || !!product.requiresSellingPlan);
  // Subscribe is pre-selected whenever it's offered, defaulting to the
  // 45-day plan when Recharge carries several frequencies.
  const [selectedSellingPlanId, setSelectedSellingPlanId] = useState<string | null>(() => {
    const plans = getSubscriptionPlans(product);
    if (plans.length === 0) return null;
    const offered = subscribeSave?.enabled !== false || product.requiresSellingPlan;
    return offered ? getDefaultSellingPlan(plans)?.id ?? null : null;
  });
  const selectedSellingPlan = showSubscribeSave ? subscriptionPlans.find(plan => plan.id === selectedSellingPlanId) ?? null : null;
  const atcRef = useRef<HTMLDivElement>(null);
  const sizeSelectRef = useRef<HTMLDivElement>(null);
  // Set once the shopper (or the URL sync) explicitly picks options; an
  // explicit choice must survive the live-availability merge below.
  const hasExplicitSelectionRef = useRef(false);
  const hasSizeOption = product.options.some(opt => opt.name.toLowerCase() === 'size' && opt.values.length > 1);
  const [hasUserSelectedSize, setHasUserSelectedSize] = useState(() => !hasSizeOption);
  const currentColorOption = getColorOption(product.options);
  const showLinkedColorSelector = !!currentColorOption && linkedColorProducts.some(linkedProduct => linkedProduct.colorValues.length > 0);

  // Notify parent of selected color changes for image filtering
  const selectedColor = currentColorOption ? selectedOptions[currentColorOption.name] ?? null : null;
  useEffect(() => {
    onSelectedColorChange?.(selectedColor);
  }, [selectedColor, onSelectedColorChange]);
  const resolvedVariants = variants;
  const selectedVariant = useMemo(() => {
    return resolvedVariants.find(variant => variant.selectedOptions.every(opt_0 => selectedOptions[opt_0.name] === opt_0.value));
  }, [resolvedVariants, selectedOptions]);

  // The exact (sold-out) variant the shopper tapped — matched by the chosen value
  // plus current selections, ignoring availability (the resolver excludes it).
  const notifyVariant = useMemo(() => {
    if (!notifySelection) return null;
    const {
      name,
      value
    } = notifySelection;
    return resolvedVariants.find(variant_0 => variant_0.selectedOptions.every(opt_1 => opt_1.name === name ? opt_1.value === value : selectedOptions[opt_1.name] === opt_1.value)) ?? null;
  }, [notifySelection, resolvedVariants, selectedOptions]);

  // Once live availability lands, an untouched default selection that turned
  // out to be sold out is re-resolved to the first live-available variant —
  // the same correction the server used to apply in its second render pass.
  useEffect(() => {
    if (availabilityPending || hasExplicitSelectionRef.current) return;
    setSelectedOptions(prev => {
      const current = resolvedVariants.find(variant_1 => variant_1.selectedOptions.every(opt_2 => prev[opt_2.name] === opt_2.value));
      if (current?.availableForSale) return prev;
      const defaults = getDefaultSelectedOptions(resolvedVariants);
      return Object.keys(defaults).length > 0 ? defaults : prev;
    });
  }, [availabilityPending, resolvedVariants]);
  const hasAvailableVariants = resolvedVariants.some(variant_2 => variant_2.availableForSale);
  const isAvailable = selectedVariant?.availableForSale ?? false;
  // Treat an active notify selection as sold-out so the buy box shows SOLD OUT
  // rather than offering to add the previously selected in-stock size.
  const showSoldOutState = !hasAvailableVariants || !selectedVariant || !isAvailable || notifySelection !== null;
  // A cached sold-out claim while live availability is still streaming — render
  // a disabled ADD TO CART instead of SOLD OUT; the live pass settles the state.
  const soldOutPending = availabilityPending && showSoldOutState && notifySelection === null;
  const price = selectedVariant?.price || product.priceRange.minVariantPrice;
  const compareAtPrice = selectedVariant?.compareAtPrice;
  const isOnSale = Boolean(compareAtPrice && Number.parseFloat(compareAtPrice.amount) > Number.parseFloat(price.amount));
  const salePercent = salePercentOff(price.amount, compareAtPrice?.amount);
  // Slot one of the bundle is always this PDP's current selection — adding it
  // to the cart is what moves the ladder.
  const apparelCurrentPick = useMemo<ApparelBundlePick | null>(() => {
    if (!apparelBundle || !selectedVariant) return null;
    const unitPrice = Number(selectedVariant.price.amount);
    const unitCompareAt = selectedVariant.compareAtPrice ? Number(selectedVariant.compareAtPrice.amount) : null;
    return {
      handle: product.handle,
      productId: product.id,
      title: product.title,
      productType: product.productType,
      merchandiseId: selectedVariant.id,
      variantTitle: selectedVariant.title ?? null,
      sku: selectedVariant.sku,
      vendor: product.vendor,
      image: selectedVariant.image ?? product.featuredImage ?? null,
      price: Number.isFinite(unitPrice) ? unitPrice : 0,
      compareAt: unitCompareAt !== null && Number.isFinite(unitCompareAt) ? unitCompareAt : null,
      currencyCode: selectedVariant.price.currencyCode,
      isCurrent: true
    };
  }, [apparelBundle, selectedVariant, product]);
  const apparelBundleState = useApparelBundleState(apparelBundle, apparelCurrentPick, price.currencyCode);

  // With a subscription plan selected, the headline price is the per-delivery
  // plan price and the one-time price becomes the strikethrough reference.
  const planPercentOff = selectedSellingPlan ? getSellingPlanPercentOff(price, selectedSellingPlan) : null;
  const displayPrice = selectedSellingPlan ? getSellingPlanUnitPrice(price, selectedSellingPlan) : price;
  const displayComparePrice = selectedSellingPlan ? planPercentOff !== null ? price : null : isOnSale && compareAtPrice ? compareAtPrice : null;
  // While the bundle tab is active, the headline price mirrors the bundle
  // totals, struck through against the full four-bag retail value ($180).
  const bundlePricing = isBundleMode ? bundleState.pricing : null;
  const bundleSubscribeHeadline = !!bundlePricing?.subscribeTotal && bundleState.subscribeSelected;
  const headlinePrice = bundlePricing ? bundleSubscribeHeadline && bundlePricing.subscribeTotal ? bundlePricing.subscribeTotal : bundlePricing.oneTimeTotal : displayPrice;
  const headlineComparePrice = bundlePricing ? Number.parseFloat(bundlePricing.retailTotal.amount) > Number.parseFloat(headlinePrice.amount) ? bundlePricing.retailTotal : null : displayComparePrice;
  const headlineSavePercent = bundlePricing ? bundleSubscribeHeadline ? bundlePricing.subscribePercentOff : null : planPercentOff;
  // Per-stick tab meta follows the current subscribe/one-time selection like
  // the headline price does ($25 subscription on a 32ct bag reads $0.78 /
  // stick, not the $50 one-time $1.56). The one-bag side rides displayPrice;
  // the kit side uses the selected frequency's plan price in bundle mode and
  // the kit's default plan while the one-bag subscription is selected.
  const kitSubscribing = bundlePricing ? bundleState.subscribeSelected : Boolean(selectedSellingPlan);
  const kitDefaultPlan = bundle ? getDefaultSellingPlan(bundle.kitProduct.sellingPlans) : null;
  const kitTabPrice = bundle ? bundlePricing?.subscribeTotal && bundleState.subscribeSelected ? bundlePricing.subscribeTotal : kitSubscribing && kitDefaultPlan ? getSellingPlanUnitPrice(bundle.kitProduct.price, kitDefaultPlan) : bundle.kitProduct.price : null;
  // One box ↔ Insider Bundle switch, rendered directly above the purchase
  // options in both modes.
  const modeTabs = bundle ? <BuyBoxModeTabs mode={buyBoxMode} onChange={setBuyBoxMode} oneBoxLabel={bundle.config.oneBoxTabLabel} oneBoxMeta={`(${bundle.config.sticksPerBox}ct) · ${formatPerUnitPrice(displayPrice, bundle.config.sticksPerBox, moneyLocale)} / stick`} bundleLabel={bundle.config.bundleTabLabel} bundleMeta={`(${getTotalSticks(bundle.config)}ct) · ${formatPerUnitPrice(kitTabPrice ?? bundle.kitProduct.price, getTotalSticks(bundle.config), moneyLocale)} / stick`} bestValueBadge={bundle.config.bestValueBadge} bundleBagCount={bundle.config.boxCount + bundle.config.bonusCount} /> : null;
  const ctaLabel = selectedSellingPlan ? (subscribeSaveConfig.subscribeCtaLabel || DEFAULT_SUBSCRIBE_SAVE_SETTINGS.subscribeCtaLabel).toUpperCase() : 'ADD TO CART';
  // Thumbnail inside the subscription plan row (buy-box mockup plan thumb).
  const planThumbImage = selectedVariant?.image || product.featuredImage;
  // CMS microcopy under the trust line, with {price}/{oneTimePrice} resolved
  // against the selected (or first) plan.
  const subtextPlan = selectedSellingPlan ?? getDefaultSellingPlan(subscriptionPlans);
  const trustSubtext = showSubscribeSave && subtextPlan ? subscribeSaveConfig.microcopy.replaceAll('{price}', formatMoney(getSellingPlanUnitPrice(price, subtextPlan), moneyLocale)).replaceAll('{oneTimePrice}', formatMoney(price, moneyLocale)) : null;

  // Show sticky mobile ATC bar when the main button scrolls out of view,
  // but hide it once the footer is in view so it doesn't cover the footer
  useEffect(() => {
    const el = atcRef.current;
    if (!el) return;
    const footer = document.querySelector('footer');
    let atcAboveViewport = false;
    let footerVisible = false;
    const update = () => setShowStickyATC(atcAboveViewport && !footerVisible);
    const atcObserver = new IntersectionObserver(([entry]) => {
      if (entry) {
        // Only show sticky ATC when the button has scrolled above the viewport,
        // not when the user is above it and hasn't scrolled down to it yet
        atcAboveViewport = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        update();
      }
    }, {
      threshold: 0
    });
    atcObserver.observe(el);
    let footerObserver: IntersectionObserver | undefined;
    if (footer) {
      footerObserver = new IntersectionObserver(([entry_0]) => {
        if (entry_0) {
          footerVisible = entry_0.isIntersecting;
          update();
        }
      }, {
        threshold: 0
      });
      footerObserver.observe(footer);
    }
    return () => {
      atcObserver.disconnect();
      footerObserver?.disconnect();
    };
  }, []);
  const handleOptionChange = useCallback((name_0: string, value_0: string) => {
    hasExplicitSelectionRef.current = true;
    // Choosing any in-stock option dismisses the back-in-stock prompt.
    setNotifySelection(null);
    if (name_0.toLowerCase() === 'size') {
      setHasUserSelectedSize(true);
    }
    setShouldSyncVariantUrl(true);
    setSelectedOptions(prev_0 => {
      return resolveOptionsAfterOptionChange(resolvedVariants, prev_0, name_0, value_0);
    });
  }, [resolvedVariants]);
  const handleSelectSoldOut = useCallback((name_1: string, value_1: string) => {
    hasExplicitSelectionRef.current = true;
    if (name_1.toLowerCase() === 'size') {
      setHasUserSelectedSize(true);
    }
    setNotifySelection({
      name: name_1,
      value: value_1
    });
  }, []);
  const handleRequestedSelection = useCallback((requestedOptions: Record<string, string>) => {
    hasExplicitSelectionRef.current = true;
    setSelectedOptions(prev_1 => {
      const keys = Object.keys(requestedOptions);
      if (keys.length === Object.keys(prev_1).length && keys.every(key => prev_1[key] === requestedOptions[key])) {
        return prev_1;
      }
      return requestedOptions;
    });
    setHasUserSelectedSize(true);
  }, []);

  // Toolbox membership. The row is a checkbox: ticking it adds the Toolbox to
  // the cart right away on its monthly plan, in the tee size chosen above,
  // then opens the gift size sheet pre-set to that size so the shopper can
  // confirm or swap it (a swap replaces the line). Unticking removes it.
  // "Ticked" is read off the live cart, so it survives a reload.
  const toolboxLine = toolbox ? liveCart?.lines.edges.find(edge => edge.node.merchandise.product.handle === toolbox.handle)?.node ?? null : null;
  const [toolboxBusy, setToolboxBusy] = useState(false);
  const selectedSizeValue = Object.entries(selectedOptions).find(([name_2]) => /size/i.test(name_2))?.[1] ?? null;
  const addToolbox = async (variant_3: ToolboxMembershipOffer['variants'][number]) => {
    if (!toolbox) return null;
    return addItem(variant_3.id, 1, {
      productId: toolbox.productId,
      title: toolbox.title,
      handle: toolbox.handle,
      vendor: toolbox.vendor,
      productType: toolbox.productType,
      image: toolbox.image,
      variantTitle: variant_3.size,
      sku: variant_3.sku,
      price: toolbox.memberPrice
    }, {
      sellingPlan: toolbox.plan,
      analyticsSource: 'pdp_toolbox_membership',
      openCart: false
    });
  };
  const handleToolboxChange = async (checked: boolean) => {
    if (!toolbox || toolboxBusy) return;
    setToolboxBusy(true);
    try {
      if (!checked) {
        if (toolboxLine) await removeItem(toolboxLine.id);
        return;
      }
      const size = pickToolboxSize(toolbox, selectedSizeValue);
      const variant = toolbox.variants.find(entry => entry.size === size && entry.availableForSale);
      if (!variant) return;
      const nextCart = await addToolbox(variant);
      if (!nextCart) return;
      setGiftSelections([{
        productHandle: toolbox.handle,
        title: toolbox.title,
        initialVariantId: variant.id,
        eyebrow: 'Toolbox membership · confirm your tee size',
        ctaLabel: 'Confirm size',
        hideSkip: true,
        price: {
          ...toolbox.memberPrice,
          cadence: formatMembershipCadence(toolbox.frequencyPhrase)
        },
        details: TOOLBOX_MEMBERSHIP_DETAILS,
        commit: async picked => {
          if (picked.id === variant.id) return;
          const current = getCart()?.lines.edges.find(edge => edge.node.merchandise.product.handle === toolbox.handle)?.node;
          if (current) await removeItem(current.id);
          const swapped = toolbox.variants.find(entry => entry.id === picked.id);
          if (swapped) await addToolbox(swapped);
        }
      }]);
    } finally {
      setToolboxBusy(false);
    }
  };
  const handleAddToCart = async () => {
    if (!selectedVariant || !isAvailable || isAddingToCart) return;

    // Gifts come with the ORDER, not with every subscription: only the first
    // subscription line in the cart brings its gifts along.
    const cart = getCart();
    const cartHasSubscription = !!cart?.lines.edges.some(edge_0 => edge_0.node.sellingPlanAllocation);
    const giftsApply = !!selectedSellingPlan && !cartHasSubscription;
    const payload = buildAddToCartPayload({
      product,
      selectedVariant,
      selectedSellingPlan,
      giftLines: giftsApply ? buildGiftExtraLines(subscribeSaveConfig.gifts, cart, price.currencyCode) : []
    });
    setIsAddingToCart(true);
    try {
      const nextCart = await addItem(selectedVariant.id, 1, payload.productInfo, payload.options);
      if (!nextCart) return;
      if (giftsApply) {
        setGiftSelections(buildGiftSelections(subscribeSaveConfig.gifts, nextCart));
      }
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 2000);
    } finally {
      setIsAddingToCart(false);
    }
  };
  return <>
			{GiftVariantModal && <GiftVariantModal gifts={giftSelections} open={giftSelections.length > 0} onOpenChange={open => {
      if (!open) setGiftSelections([]);
    }} />}
			<Suspense fallback={null}>
				<ProductVariantSearchParamsSync productHandle={product.handle} options={product.options} variants={resolvedVariants} selectedVariant={selectedVariant} syncVariantUrl={shouldSyncVariantUrl} onRequestedSelection={handleRequestedSelection} />
			</Suspense>
			<div className="flex flex-col">
				{/* Star Rating + Sale badge */}
				<div className="flex items-center justify-between">
					{reviewSummarySlot ?? (enableReviews && ratingProp && <StarRating rating={ratingProp} count={reviewCountProp ?? 0} />)}
					{isOnSale && <span className="rounded bg-brand-accent px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
							{salePercent ? `Sale · ${salePercent}%` : 'Sale'}
						</span>}
				</div>

				{/* Title */}
				<h1 className="mt-2 text-[28px] font-extrabold uppercase leading-none tracking-[-0.02em] lg:text-[31px]">
					{displayTitle ?? product.title}
				</h1>

				{/* Price — superscript cents; reflects the selected subscription plan,
        or the bundle totals while the Insider Bundle tab is active */}
				<div className="mt-2 flex items-end gap-2.5">
					<PriceWithCents amount={headlinePrice.amount} currencyCode={headlinePrice.currencyCode} locale={moneyLocale} className="text-[22px] font-extrabold leading-none tracking-[-0.02em] text-neutral-900" />
					{headlineComparePrice && <span className="pb-px text-sm leading-none text-neutral-500 line-through">
							{formatMoney(headlineComparePrice, moneyLocale)}
						</span>}
					{headlineSavePercent !== null && <span className="mb-0.5 rounded-sm bg-brand-accent px-[7px] py-1 text-[11px] font-bold uppercase leading-none tracking-wider text-white">
							Save {headlineSavePercent}%
						</span>}
				</div>

				{/* CMS tagline under the price (per product type) */}
				{typeDetails?.tagline && <p className="mt-3 text-sm leading-relaxed text-neutral-600">{typeDetails.tagline}</p>}

				{isBundleMode && bundle && HydrateBundlePanel && <div className="mt-6">
						<HydrateBundlePanel state={bundleState} subscribeSaveConfig={subscribeSaveConfig} moneyLocale={moneyLocale} onGiftSelections={setGiftSelections} modeTabs={modeTabs} />
						<div className="mt-5">
							{featuredReviewSlot ?? <FeaturedReview review={featuredReview} />}
						</div>
					</div>}

				{!isBundleMode && <div className="mt-6 space-y-5">
						{/* Style Selector (sibling products) */}
						{siblings.length > 1 && <ProductStyleSelector currentHandle={product.handle} siblings={siblings} selectedOptions={selectedOptions} />}

						{/* One bag ↔ Crew Pack switch sits above the flavor swatches so the
          format is chosen before the flavor */}
						{modeTabs}

						{/* Print/pattern or flavor swatches (separate products, not Shopify options) */}
						{swatchGroup.length > 1 && <ProductSwatchGroup items={swatchGroup} groupLabel={swatchGroupLabel} />}

						{showLinkedColorSelector && <LinkedProductColorSelector currentHandle={product.handle} options={product.options} variants={resolvedVariants} linkedProducts={linkedColorProducts} selectedOptions={selectedOptions} onColorChange={handleOptionChange} />}

						{/* Variant Selector */}
						{product.options.length > 0 && !(product.options.length === 1 && product.options[0]?.values.length === 1) && <div ref={sizeSelectRef}>
									<VariantSelector options={product.options} selectedOptions={selectedOptions} variants={resolvedVariants} onOptionChange={handleOptionChange} hiddenOptionNames={showLinkedColorSelector && currentColorOption ? [currentColorOption.name] : []} soldOutSelection={notifySelection} onSelectSoldOut={handleSelectSoldOut} />
								</div>}

						{/* Fit indicator */}
						<FitIndicator typeDetails={typeDetails} sizingChartSlot={sizingChartSlot} />

						{/* Subscribe & Save — only for products with recurring selling plans */}
						{showSubscribeSave && PurchaseOptionSelector && <PurchaseOptionSelector plans={subscriptionPlans} price={price} selectedPlanId={selectedSellingPlanId} onSelect={setSelectedSellingPlanId} requiresSellingPlan={product.requiresSellingPlan} thumbnail={planThumbImage ? {
          url: planThumbImage.url,
          alt: planThumbImage.altText || product.title
        } : null} config={subscribeSaveConfig} moneyLocale={moneyLocale} />}

						{/* Add to Cart — burnt-orange CTA */}
						<div ref={atcRef} className="space-y-3">
							<div>
								<button type="button" className={`w-full rounded py-4 text-base font-bold uppercase tracking-[0.08em] transition-all ${!showSoldOutState || soldOutPending ? 'bg-brand-accent text-white hover:brightness-110' : 'cursor-not-allowed bg-neutral-300 text-neutral-500'}`} onClick={handleAddToCart} disabled={showSoldOutState || isAddingToCart}>
									{!showSoldOutState ? isAddingToCart ? <span className="flex items-center justify-center">
												<svg className="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
													<title>Loading</title>
													<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
													<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
												</svg>
											</span> : ctaLabel : soldOutPending ? ctaLabel : 'SOLD OUT'}
								</button>
							</div>

							{/* Toolbox membership — directly under the CTA, as on the original
           storefront: tick it and the Toolbox goes straight into the cart. */}
							{toolbox && !showSoldOutState && <ToolboxMembershipRow offer={toolbox} checked={Boolean(toolboxLine)} disabled={toolboxBusy} onCheckedChange={checked_0 => void handleToolboxChange(checked_0)} moneyLocale={moneyLocale} />}

							{/* Bundle ladder — a quiet strip under the CTA that mirrors the
           cart. Adding this product is what moves it. */}
							{apparelBundle && !showSoldOutState && <ApparelBundleStrip state={apparelBundleState} moneyLocale={moneyLocale} />}

							{/* Back in stock — notify-me button under the SOLD OUT button */}
							{notifyVariant && !notifyVariant.availableForSale && <BackInStockNotify key={notifyVariant.id} variant={notifyVariant} productTitle={product.title} />}

							{/* Under-CTA trust: subscription products get the gifts/guarantee/reviews
          checklist here; everything else shows its badges under the review. */}
							{showSubscribeSave && <PurchaseTrustList items={subscribeSaveConfig.benefits.map(benefit => benefit.text)} subtext={trustSubtext} />}
						</div>

						{/* Featured review testimonial */}
						{featuredReviewSlot ?? <FeaturedReview review={featuredReview} />}

						{/* Trust badges — CMS-driven per product type, falling back to the
          built-in pair. Under the review so the bundle strip sits directly
          against the CTA it extends. */}
						{!showSubscribeSave && <div className="flex items-center justify-center gap-3 text-xs font-semibold uppercase tracking-wider text-neutral-700">
								{(trustBadges?.length ? trustBadges : DEFAULT_TRUST_BADGES).map((badge, index) => <Fragment key={badge.label}>
										{index > 0 && <span className="text-neutral-300">+</span>}
										<span className="flex items-center gap-1.5">
											<TrustBadgeIcon icon={badge.icon} />
											{badge.label}
										</span>
									</Fragment>)}
							</div>}
					</div>}
			</div>

			{/* Sticky mobile ATC bar — compact single row mirroring the buy-box price treatment.
       Hidden in bundle mode: the bundle CTA manages its own state. */}
			{showStickyATC && !isCartOpen && !isBundleMode && <div className="sticky-atc-bar fixed bottom-0 left-0 right-0 z-50 border-t border-neutral-200 bg-white px-4 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] shadow-[0_-4px_16px_rgba(0,0,0,0.08)] lg:hidden">
					<div className="flex items-center gap-3">
						{planThumbImage && <ShopifyImage src={planThumbImage.url} alt={planThumbImage.altText || product.title} width={44} height={44} sizes="44px" className="h-11 w-11 shrink-0 rounded-md bg-neutral-100 object-cover" />}
						<div className="min-w-0 flex-1">
							<p className="truncate text-[13px] font-bold uppercase leading-tight tracking-wide">
								{displayTitle ?? product.title}
							</p>
							<div className="mt-1 flex items-center gap-1.5">
								<PriceWithCents amount={displayPrice.amount} currencyCode={displayPrice.currencyCode} locale={moneyLocale} className="text-[15px] font-extrabold leading-none tracking-[-0.02em] text-neutral-900" />
								{displayComparePrice && <span className="text-xs leading-none text-neutral-400 line-through">
										{formatMoney(displayComparePrice, moneyLocale)}
									</span>}
								{planPercentOff !== null && <span className="rounded-sm bg-brand-accent px-1.5 py-[3px] text-[10px] font-bold uppercase leading-none tracking-wider text-white">
										Save {planPercentOff}%
									</span>}
							</div>
						</div>
						<button type="button" className={`shrink-0 whitespace-nowrap rounded-md px-5 py-3.5 text-[13px] font-bold uppercase tracking-[0.08em] transition-all ${!hasUserSelectedSize || !showSoldOutState || soldOutPending ? 'bg-brand-accent text-white hover:brightness-110' : 'cursor-not-allowed bg-neutral-300 text-neutral-500'}`} onClick={!hasUserSelectedSize ? () => sizeSelectRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        }) : handleAddToCart} disabled={hasUserSelectedSize && (showSoldOutState || isAddingToCart)}>
							{!hasUserSelectedSize ? 'SELECT SIZE' : !showSoldOutState ? isAddingToCart ? <svg className="mx-auto h-[18px] w-[18px] animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
										<title>Loading</title>
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
										<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
									</svg> : ctaLabel : soldOutPending ? ctaLabel : 'SOLD OUT'}
						</button>
					</div>
				</div>}
		</>;
}
function ProductVariantSearchParamsSync({
  productHandle,
  options,
  variants,
  selectedVariant,
  syncVariantUrl,
  onRequestedSelection
}: {
  productHandle: string;
  options: ProductOption[];
  variants: ProductVariant[];
  selectedVariant?: ProductVariant;
  syncVariantUrl: boolean;
  onRequestedSelection: (requestedOptions: Record<string, string>) => void;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchParamsString = searchParams.toString();
  const productHandleRef = useRef(productHandle);
  const lastAppliedSearchParamsRef = useRef<string | null>(null);
  const lastWrittenSearchParamsRef = useRef<string | null>(null);
  const pendingRequestedSelectionRef = useRef<PendingRequestedSelection | null>(null);
  if (productHandleRef.current !== productHandle) {
    productHandleRef.current = productHandle;
    lastAppliedSearchParamsRef.current = null;
    lastWrittenSearchParamsRef.current = null;
    pendingRequestedSelectionRef.current = null;
  }
  useBrowserLayoutEffect(() => {
    if (lastWrittenSearchParamsRef.current === searchParamsString) return;
    if (lastAppliedSearchParamsRef.current === searchParamsString) return;
    lastAppliedSearchParamsRef.current = searchParamsString;
    const requestedSelectedOptions = getSelectedOptionsFromSearchParams(new URLSearchParams(searchParamsString), variants, options);
    if (Object.keys(requestedSelectedOptions).length === 0) return;
    pendingRequestedSelectionRef.current = {
      searchParamsString,
      selectedOptions: requestedSelectedOptions
    };
    onRequestedSelection(requestedSelectedOptions);
  }, [onRequestedSelection, options, searchParamsString, variants]);
  useEffect(() => {
    if (!selectedVariant) return;
    if (!isProductPathForHandle(pathname, productHandle)) return;
    const pendingRequestedSelection = pendingRequestedSelectionRef.current;
    if (shouldWaitForRequestedSelection(selectedVariant, pendingRequestedSelection, searchParamsString)) {
      return;
    }
    if (pendingRequestedSelection?.searchParamsString === searchParamsString) {
      pendingRequestedSelectionRef.current = null;
    }
    const syncTarget = getVariantUrlSyncTarget({
      pathname,
      searchParamsString,
      selectedVariant,
      syncVariantUrl
    });
    if (!syncTarget) return;
    lastWrittenSearchParamsRef.current = syncTarget.query;
    replaceBrowserUrlWithoutNavigation(syncTarget.url);
  }, [pathname, productHandle, searchParamsString, selectedVariant, syncVariantUrl]);
  return null;
}